import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import UserNavbar from '../components/UserNavbar';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

const SPECIALIZATION_OPTIONS = [
  { value: '', label: 'All Specializations' },
  { value: 'Cardiologist', label: 'Cardiologist' },
  { value: 'Dermatologist', label: 'Dermatologist' },
  { value: 'Pediatrician', label: 'Pediatrician' },
  { value: 'Orthopedic', label: 'Orthopedic' },
  { value: 'Neurologist', label: 'Neurologist' },
  { value: 'General Physician', label: 'General Physician' },
];

function UserFavorites() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 FILTER STATES
  const [filters, setFilters] = useState({
    location: '',
    specialization: ''
  });

  useEffect(() => {
    fetchFavorites();
  }, [filters]);

  const fetchFavorites = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.location && filters.location.trim()) {
        queryParams.append('location', filters.location.trim());
      }
      if (filters.specialization && filters.specialization.trim()) {
        queryParams.append('specialization', filters.specialization.trim());
      }

      const res = await api.get(`user/favorites/?${queryParams.toString()}`);
      setFavorites(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/user/login');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (doctorId) => {
    try {
      await api.post('user/favorites/remove/', { doctor_id: doctorId });
      fetchFavorites();
    } catch (err) {
      toast.error("Failed to remove from favorites", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };
  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return "https://via.placeholder.com/300x200?text=Doctor";

    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    if (image.startsWith('/media/')) {
      return `${apiUrl}${image}`;
    }

    // Add /media/ prefix for paths like "doctor_images/filename.png"
    return `${apiUrl}/media/${image}`;
  };
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <UserNavbar />
      <div className="container py-5">
        <div className="row mb-4">
          <div className="col">
            <h2 className="fw-bold"><i className="bi bi-heart-fill me-2 text-danger"></i>Favorite Doctors</h2>
            <p className="text-muted">Quickly access your preferred medical specialists.</p>
          </div>
        </div>

        {/* 🔹 FILTERS SECTION */}
        <div className="card border-0 shadow-sm rounded-4 p-4 mb-5">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label small fw-bold text-muted">Location</label>
              <div className="input-group">
                <span className="input-group-text bg-light border-0"><i className="bi bi-geo-alt"></i></span>
                <input
                  type="text"
                  className="form-control bg-light border-0"
                  placeholder="Filter by city/area..."
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                />
              </div>
            </div>
            <div className="col-md-5">
              <label className="form-label small fw-bold text-muted">Specialization</label>
              <FilterSelect
                value={filters.specialization}
                onChange={(specialization) => setFilters({ ...filters, specialization })}
                options={SPECIALIZATION_OPTIONS}
                placeholder="All Specializations"
                aria-label="Specialization"
                variant="light"
              />
            </div>
            <div className="col-md-1 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary w-100 fw-bold"
                onClick={() => setFilters({ location: '', specialization: '' })}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="row g-4">
          {favorites.length === 0 ? (
            <div className="col-12 text-center py-5">
              <div className="card border-0 shadow-sm rounded-4 p-5">
                <i className="bi bi-heart fs-1 text-muted mb-3 d-block"></i>
                <h5>You haven't added any favorites yet</h5>
                <p className="text-muted">Browse doctors and click the heart icon to save them here.</p>
                <div className="mt-3">
                  <button className="btn btn-primary rounded-pill px-4" onClick={() => navigate('/user/doctor_list')}>
                    Browse Doctors
                  </button>
                </div>
              </div>
            </div>
          ) : (
            favorites.map((fav) => (
              <div key={fav.id || fav.doctor_id} className="col-md-6 col-lg-4 col-xl-3">
                <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                  <img
                    src={getImageUrl(fav.image)}
                    alt={fav.name}
                    className="card-img-top object-fit-cover"
                    style={{ height: '200px' }}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/300x200?text=Doctor";
                    }}
                  />
                  <div className="card-body p-4">
                    <h6 className="fw-bold mb-1"> {fav.name}</h6>
                    <p className="text-primary small fw-bold text-uppercase mb-3">{fav.specialization || 'General Physician'}</p>
                    {/* <p className="text-primary small fw-bold text-uppercase mb-3">{fav.experience || '0'}</p> */}

                    <div className="d-grid gap-2">
                      <button
                        className="btn btn-primary btn-sm rounded-pill"
                        onClick={() => navigate(`/user/book-appointment/${fav.doctor_id}`)}
                      >
                        Book Now
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm rounded-pill"
                        onClick={() => removeFavorite(fav.doctor_id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default UserFavorites;
